import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, ArrowUpRight } from "lucide-react";
import {
  EMAIL,
  FACEBOOK_URL,
  TIKTOK_URL,
  INSTAGRAM_URL,
  PHONE,
  PHONE_TEL,
  LOGO_URL,
} from "../lib/utils";
import PreReservationDialog from "./PreReservationDialog";

// TikTok icon (lucide doesn't ship one)
const TikTokIcon = ({ className = "", strokeWidth = 2.5 }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#0a0a0a] text-white pt-20 md:pt-28 pb-10 overflow-hidden"
    >
      {/* Top CTA strip */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="border-2 border-[#E91E63] p-6 md:p-10 lg:p-12 mb-16 md:mb-20 grid lg:grid-cols-12 gap-6 items-center">
          <div className="lg:col-span-8">
            <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-[#E91E63] mb-3">
              Prêt à dégainer ?
            </p>
            <p className="font-display text-3xl md:text-4xl lg:text-5xl font-bold uppercase leading-[0.95] tracking-tight">
              Réservez votre session
              <br />
              <span className="text-[#E91E63]">à Fougères ou Louvigné</span>
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <PreReservationDialog
              trigger={
                <button
                  data-testid="footer-reserve-button"
                  className="inline-flex items-center justify-between gap-3 px-6 py-4 bg-[#E91E63] text-white font-bold uppercase text-sm tracking-wider hover:-translate-y-0.5 hover:shadow-[0_8px_0_-2px_#FFEB3B] transition-all w-full"
                >
                  Pré-réservation en ligne
                  <ArrowUpRight className="w-5 h-5" strokeWidth={2.5} />
                </button>
              }
            />
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="footer-call-button"
              className="inline-flex items-center justify-between gap-3 px-6 py-4 border-2 border-white text-white font-bold uppercase text-sm tracking-wider hover:bg-white hover:text-black transition-colors"
            >
              <span className="flex items-center gap-2">
                <Phone className="w-4 h-4" strokeWidth={2.5} /> {PHONE}
              </span>
              <ArrowUpRight className="w-5 h-5" strokeWidth={2.5} />
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-5">
              <img
                src={LOGO_URL}
                alt="Logo Paintball Aventures 35 — réserver paintball 35"
                className="w-14 h-14 object-contain"
                loading="lazy"
              />
              <p className="font-display text-xl font-bold uppercase tracking-tight">
                Paintball Aventures 35
              </p>
            </div>
            <p className="text-sm text-white/70 leading-relaxed max-w-sm">
              Spécialiste du paintball à Fougères et Louvigné-du-Désert, en plein cœur de l&apos;Ille-et-Vilaine. Plus de 20 ans d&apos;expérience au service du fun.
            </p>
            <a
              href={FACEBOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-facebook-link"
              className="hidden"
              aria-hidden="true"
              tabIndex={-1}
            >
              Facebook
            </a>

            {/* Social icons strip */}
            <div className="mt-5 flex items-center gap-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mr-1">
                Suivez-nous
              </p>
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook Paintball Aventures 35"
                data-testid="social-facebook"
                className="w-10 h-10 inline-flex items-center justify-center border border-white/25 hover:bg-[#E91E63] hover:border-[#E91E63] transition-colors"
              >
                <Facebook className="w-4 h-4" strokeWidth={2.5} />
              </a>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram Paintball Aventures 35"
                data-testid="social-instagram"
                className="w-10 h-10 inline-flex items-center justify-center border border-white/25 hover:bg-[#E91E63] hover:border-[#E91E63] transition-colors"
              >
                <Instagram className="w-4 h-4" strokeWidth={2.5} />
              </a>
              <a
                href={TIKTOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TikTok Paintball Aventures 35"
                data-testid="social-tiktok"
                className="w-10 h-10 inline-flex items-center justify-center border border-white/25 hover:bg-[#E91E63] hover:border-[#E91E63] transition-colors"
              >
                <TikTokIcon className="w-4 h-4" strokeWidth={2.5} />
              </a>
            </div>
          </div>

          {/* Hours */}
          <div className="lg:col-span-3">
            <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-[#E91E63] mb-4">
              Horaires
            </p>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2.5">
                <Clock className="w-4 h-4 mt-0.5 text-[#E91E63] shrink-0" strokeWidth={2.5} />
                <div>
                  <p className="font-bold">Lundi → Vendredi</p>
                  <p className="text-white/70">10:00 — 20:00</p>
                </div>
              </li>
              <li className="flex items-start gap-2.5">
                <Clock className="w-4 h-4 mt-0.5 text-[#E91E63] shrink-0" strokeWidth={2.5} />
                <div>
                  <p className="font-bold">Samedi & Dimanche</p>
                  <p className="text-white/70">Ouvert 24h/24</p>
                  <p className="text-xs text-[#FFEB3B] mt-0.5">
                    Uniquement sur réservation
                  </p>
                </div>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-[#E91E63] mb-4">
              Contact
            </p>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  href={`tel:${PHONE_TEL}`}
                  data-testid="footer-phone-link"
                  className="flex items-center gap-2.5 hover:text-[#E91E63] transition-colors group"
                >
                  <Phone className="w-4 h-4 text-[#E91E63]" strokeWidth={2.5} />
                  <span className="font-display text-lg font-bold tracking-wide">
                    {PHONE}
                  </span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${EMAIL}`}
                  data-testid="footer-email-link"
                  className="flex items-center gap-2.5 hover:text-[#E91E63] transition-colors"
                >
                  <Mail className="w-4 h-4 text-[#E91E63]" strokeWidth={2.5} />
                  <span className="break-all">{EMAIL}</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Sites */}
          <div className="lg:col-span-2">
            <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-[#E91E63] mb-4">
              Nos sites
            </p>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 mt-0.5 text-[#E91E63] shrink-0" strokeWidth={2.5} />
                <div>
                  <p className="font-bold">Fougères</p>
                  <p className="text-white/70 text-xs">
                    20 Bd Nelson Mandela 35300
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 mt-0.5 text-[#E91E63] shrink-0" strokeWidth={2.5} />
                <div>
                  <p className="font-bold">Louvigné-du-Désert</p>
                  <p className="text-white/70 text-xs">La Morinais 35420</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-16 pt-8 border-t border-white/15 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-white/50">
          <p>
            © {new Date().getFullYear()} Paintball Aventures 35 — Paintball Fougères &amp; Louvigné-du-Désert.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <Link
              to="/mentions-legales"
              data-testid="footer-mentions-legales"
              className="hover:text-[#E91E63] transition-colors underline-offset-2 hover:underline"
            >
              Mentions légales
            </Link>
            <span className="text-white/20">·</span>
            <p className="font-mono uppercase tracking-[0.2em]">
              Paintball Fougères · Louvigné-du-Désert · 35
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
